import {Component, computed, effect, signal, ViewChild} from '@angular/core';
import {NavigationEnd, NavigationStart, Router, RouterOutlet} from '@angular/router';
import {TranslatorService} from './services/translator.service';
import {TranslateModule} from '@ngx-translate/core';
import {HeaderComponent} from './components/header/header.component';
import {LoadingSpinnerService} from './services/loading-spinner.service';
import {AsyncPipe, NgClass} from '@angular/common';
import {SessionService} from './services/session.service';
import {ThemeService} from './services/theming.service';
import {SideNavigationComponent} from './components/side-navigation/side-navigation.component';
import {AuthService} from './services/auth.service';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {filter} from 'rxjs';
import {UserContextFacadeService} from './services/user-context-facade.service';
import {MatIcon} from '@angular/material/icon';
import {MatIconButton} from '@angular/material/button';
import {MatDrawer, MatDrawerContainer, MatDrawerContent} from '@angular/material/sidenav';
import {CommentsComponent} from './components/comments/comments.component';
import {CaseDataComponent} from './components/case-data/case-data.component';
import {CaseActionsComponent} from './components/case-actions/case-actions.component';
import {CommentContextService} from './services/comment-context.service';
import {AuditHistoryComponent} from './components/audit-history/audit-history.component';

export type RightSidePanel = 'comments' | 'data' | 'actions' | 'audit';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    TranslateModule,
    HeaderComponent,
    SideNavigationComponent,
    NgClass,
    MatProgressSpinner,
    AsyncPipe,
    MatIcon,
    MatIconButton,
    MatDrawer,
    MatDrawerContainer,
    MatDrawerContent,
    CommentsComponent,
    CaseDataComponent,
    CaseActionsComponent,
    AuditHistoryComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
  authenticatedAndTeamChosen: boolean = false;
  isPanelView: boolean = false;
  readonly activePanel = signal<RightSidePanel>('comments');
  readonly commentsEnabled = computed(() => !!this.commentContextService.panelHal()?._links.getLink('getComments'));
  readonly customDataEnabled = computed(() => !!this.commentContextService.panelHal()?._links.getLink('getCustomData'));
  readonly customActionsEnabled = computed(() => !!this.commentContextService.panelHal()?._links.getLink('getCustomActions'));
  readonly auditEnabled = computed(() => !!this.commentContextService.panelHal()?._links.getLink('getAudit'));
  readonly anyPanelEnabled = computed(() => this.commentsEnabled() || this.customDataEnabled() || this.customActionsEnabled() || this.auditEnabled());
  private readonly panelEnabled: Record<RightSidePanel, () => boolean> = {
    comments: this.commentsEnabled,
    data: this.customDataEnabled,
    actions: this.customActionsEnabled,
    audit: this.auditEnabled
  };

  @ViewChild('rightSideDrawer') private readonly rightSideDrawer!: MatDrawer;

  // Matches the case detail view and the document preview view; both host the right-side panels.
  private static readonly PANEL_VIEW_PATTERN = /\/teams\/[^/]+\/cases\/[^/]+(\/documents\/[^/]+)?$/;

  constructor(
    public loadingSpinnerService: LoadingSpinnerService,
    private readonly sessionService: SessionService,
    private readonly router: Router,
    private readonly commentContextService: CommentContextService,
    translatorService: TranslatorService,
    themeService: ThemeService,
    authService: AuthService,
    // is used to initialize the synchronization between auth and session state - do not remove
    userContextFacadeService: UserContextFacadeService
  ) {
    themeService.loadCustomerTheme();
    loadingSpinnerService.show();
    translatorService.defineUsedLanguage();
    effect(() => {
      this.authenticatedAndTeamChosen = this.sessionService?.getSession()?.currentTeam != undefined && authService.isAuthenticated();
    });
    effect(() => {
      if (!this.panelEnabled[this.activePanel()]()) {
        this.rightSideDrawer?.close();
      }
    });
    this.defineQueryParamInterceptor();
    this.trackCaseDetailRoute();
  }

  togglePanel(panel: RightSidePanel) {
    if (this.rightSideDrawer.opened && this.activePanel() === panel) {
      this.rightSideDrawer.close();
      return;
    }
    this.activePanel.set(panel);
    this.rightSideDrawer.open();
  }

  // Intercepts navigation events to strip query parameters from URLs and with it prevent routing mismatches.
  defineQueryParamInterceptor() {
    this.router.events
      .pipe(filter(e => e instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        const url = event.url;
        if (!url.includes("exceptionSubject") && url.includes('?') || url.includes('%3F')) {
          let cleanUrl = url.split('?')[0]; // drop query params
          cleanUrl = cleanUrl.split('%3F')[0]; // drop encoded query params
          // replace current navigation without pushing new history entry
          this.router.navigateByUrl(cleanUrl, {replaceUrl: true}).then();
        }
      });
  }

  private trackCaseDetailRoute() {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.isPanelView = AppComponent.PANEL_VIEW_PATTERN.test(event.urlAfterRedirects);
        if (!this.isPanelView) {
          this.rightSideDrawer?.close();
        }
      });
  }
}
